/**
 * Mock workflow list — used until the Django `/api/meraki/workflows/`
 * endpoint is wired. The shape matches `WorkflowSchema` exactly so the
 * UI exercises the same code path it will against the real API.
 *
 * Keep this file as-is when the API ships — the workflows store will
 * fall back to it whenever fetch fails, so we never show an empty page
 * during an API outage.
 */
import type { Workflow } from "@/api/schemas/meraki";

export const MOCK_WORKFLOWS: Workflow[] = [
  {
    slug: "vlan-cleanup-v3",
    name: "Standard guest VLAN cleanup",
    description:
      "Removes legacy guest VLAN 50, retags all guest ports to 51, and regenerates ACLs. Safe for sites already on the v2024 baseline.",
    path: "/meraki/provisioning/run/vlan-cleanup-v3",
    recommendedScore: 92,
    scoreReasons: [
      "12 of 14 selected sites still on legacy VLAN 50",
      "No conflicting maintenance windows in the next 24h",
      "Workflow validated against the Marriott-NYC profile last week",
    ],
  },
  {
    slug: "vlan-retag-fw-bump",
    name: "VLAN re-tag · guest 50 → 51 + AP firmware bump",
    description:
      "Combines the VLAN re-tag with an AP firmware bump to MR 30.13. Validated against the Marriott-NYC profile.",
    path: "/meraki/provisioning/run/vlan-retag-fw-bump",
    recommendedScore: 88,
    scoreReasons: [
      "All target APs eligible for MR 30.13",
      "Combines two changes into a single maintenance window",
    ],
  },
  {
    slug: "hotel-cutover-v7",
    name: "Hotel cutover · full IDF refresh",
    description:
      "Full IDF reconfiguration for hotel cutovers. Re-pushes every VLAN, port and SSID. Use only during a confirmed maintenance window.",
    path: "/meraki/provisioning/run/hotel-cutover-v7",
    recommendedScore: 78,
    scoreReasons: [
      "1 of 3 selected sites scheduled for cutover this week",
      "High blast radius — requires explicit window",
    ],
  },
  {
    slug: "corp-ssid-dot1x",
    name: "Add corporate SSID + 802.1X bind",
    description:
      "Adds the corporate SSID to all APs in scope and binds it to the Radius server defined in env.",
    path: "/meraki/provisioning/run/corp-ssid-dot1x",
    recommendedScore: 64,
    scoreReasons: [
      "Radius server reachable from selected sites",
      "Some APs already have the SSID — will be reconciled",
    ],
  },
  {
    slug: "ap-mass-relocate",
    name: "Mass AP relocation (room-aware)",
    description:
      "Re-binds APs to room-aware tags after a property remodel. Reads the latest room map from the workbook.",
    path: "/meraki/provisioning/run/ap-mass-relocate",
    recommendedScore: 51,
    scoreReasons: [
      "Workbook updated within last 24h",
      "Property remodel marker detected for 1 site",
    ],
  },
  {
    slug: "switch-stack-recompose",
    name: "Switch stack recompose",
    description:
      "Re-elects the stack master, redistributes role priorities and verifies port-channel health on every member.",
    path: "/meraki/provisioning/run/switch-stack-recompose",
    recommendedScore: 38,
    scoreReasons: [
      "No recent stack health alerts — low urgency",
      "Last recompose was 84 days ago",
    ],
  },
];
